import { DateTime } from "luxon";

/**
 * Every date of the dialer is read in Europe/Paris, with UTC available as a tooltip.
 * The engine's discipline is to store UTC and reason in local time, and this is the
 * spot where the frontend most easily gets it wrong, so there is exactly one helper.
 */
export const DIALER_ZONE = "Europe/Paris";

export function parisDateTime(iso: string | null | undefined): DateTime | null {
    if (!iso) return null;
    const parsed = DateTime.fromISO(iso, { setZone: true });
    return parsed.isValid ? parsed.setZone(DIALER_ZONE) : null;
}

export function formatParis(iso: string | null | undefined, format: string = "dd/MM/yyyy HH:mm"): string {
    return parisDateTime(iso)?.toFormat(format) ?? "—";
}

/**
 * A day picked in a calendar, as the instant that opens or closes it in Paris. The day
 * picker hands back a `Date` at browser-local midnight, and a filter bound read as a
 * browser instant would drop the first or last hours of the day the user clicked.
 */
export function parisDayStart(day: Date): string {
    return DateTime.fromJSDate(day).setZone(DIALER_ZONE, { keepLocalTime: true }).startOf("day").toISO() ?? "";
}

export function parisDayEnd(day: Date): string {
    return DateTime.fromJSDate(day).setZone(DIALER_ZONE, { keepLocalTime: true }).endOf("day").toISO() ?? "";
}

/** The reverse: the Paris calendar day an instant falls on, as a day picker selects it. */
export function parisCalendarDay(iso: string | null | undefined): Date | undefined {
    const parsed = parisDateTime(iso);
    return parsed ? parsed.setZone("local", { keepLocalTime: true }).startOf("day").toJSDate() : undefined;
}

/** Longer, sentence-friendly form: "Fri 06 Mar, 09:05". */
export function formatParisLong(iso: string | null | undefined): string {
    return parisDateTime(iso)?.toFormat("ccc dd LLL, HH:mm") ?? "—";
}

export function formatParisDay(iso: string | null | undefined): string {
    return parisDateTime(iso)?.toFormat("cccc dd LLLL yyyy") ?? "Unknown date";
}

export function utcTooltip(iso: string | null | undefined): string {
    const parsed = iso ? DateTime.fromISO(iso, { setZone: true }) : null;
    if (!parsed?.isValid) return "Unknown date";
    return `${parsed.toUTC().toFormat("dd/MM/yyyy HH:mm:ss")} UTC`;
}

/**
 * Why a requested date became a later one. Keys are the engine's reasons; anything
 * else is humanised, so a reason added later reads acceptably instead of vanishing.
 */
const RESOLUTION_REASONS: Record<string, (calendar?: string | null) => string> = {
    calendar_closed: calendar => (calendar ? `pushed to the next opening of calendar “${calendar}”` : "pushed to the next calendar opening"),
    weekend: () => "deferred over the weekend",
    guard_delay: () => "held back by the guard delay",
    assignee_unavailable: () => "the assignee was unavailable",
    anti_spam: () => "held back by anti-spam",
    no_call_capacity: () => "waiting for a free call slot"
};

export function humanizeToken(token: string | null | undefined): string {
    if (!token) return "";
    const spaced = token.replace(/[_.-]+/g, " ").trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function resolutionReasonText(reason: string | null | undefined, calendar?: string | null): string | null {
    if (!reason) return null;
    const known = RESOLUTION_REASONS[reason];
    return known ? known(calendar) : humanizeToken(reason).toLowerCase();
}

/**
 * The resolved date first, the requested one second. Both come from the engine
 * (`Schedule.requested_at` / `scheduled_at`, `Awaiting.deadline`); the frontend does
 * no calendar arithmetic of its own, and must not, or it diverges from the interpreter.
 */
export function describeSchedule(params: { resolved?: string | null; requested?: string | null; reason?: string | null; calendar?: string | null }): {
    resolved: string;
    requested: string | null;
    reason: string | null;
} {
    const resolved = formatParisLong(params.resolved);
    const differs = Boolean(params.requested && params.requested !== params.resolved);
    return {
        resolved,
        requested: differs ? formatParisLong(params.requested) : null,
        reason: differs ? resolutionReasonText(params.reason, params.calendar) : null
    };
}
